import { z } from "zod";
import { compareClocks, hybridClockSchema, tickClock, type HybridClock } from "./clock";
import { entityDefaults, entitySchemas, entityTypeSchema, SETTINGS_ID, type EntityMap, type EntityType, type Settings } from "./entities";

/** One field-level change to one entity. Every mutation in ontrack is expressed as operations. */
export const syncOperationSchema = z.object({
  opId: z.string().min(1).max(128),
  deviceId: z.string().min(1).max(128),
  entityType: entityTypeSchema,
  entityId: z.string().min(1).max(128),
  clock: hybridClockSchema,
  patch: z.record(z.string(), z.unknown())
});
export type SyncOperation = z.infer<typeof syncOperationSchema>;

/** Every entity the User owns, keyed by id. */
export interface Dataset {
  settings: Settings;
  clients: Record<string, EntityMap["client"]>;
  projects: Record<string, EntityMap["project"]>;
  tasks: Record<string, EntityMap["task"]>;
  timeEntries: Record<string, EntityMap["timeEntry"]>;
  invoices: Record<string, EntityMap["invoice"]>;
  events: Record<string, EntityMap["event"]>;
}

type CollectionKey = Exclude<keyof Dataset, "settings">;
const collectionOf: Record<Exclude<EntityType, "settings">, CollectionKey> = {
  client: "clients",
  project: "projects",
  task: "tasks",
  timeEntry: "timeEntries",
  invoice: "invoices",
  event: "events"
};

const immutableFields = new Set(["id", "createdAt", "updatedAt", "fieldClocks"]);

export function emptyDataset(now = 0): Dataset {
  const settings = entitySchemas.settings.parse({ ...entityDefaults("settings"), id: SETTINGS_ID, createdAt: now, updatedAt: now, fieldClocks: {} });
  return { settings, clients: {}, projects: {}, tasks: {}, timeEntries: {}, invoices: {}, events: {} };
}

export function getEntity<T extends EntityType>(dataset: Dataset, type: T, id: string): EntityMap[T] | undefined {
  const kind: EntityType = type;
  if (kind === "settings") return dataset.settings as EntityMap[T];
  const records: Record<string, unknown> = dataset[collectionOf[kind]];
  return records[id] as EntityMap[T] | undefined;
}

export interface ApplyResult {
  dataset: Dataset;
  /** False when the operation was older than every field it touched, or produced an invalid entity. */
  changed: boolean;
  rejected: boolean;
}

/**
 * Applies one operation with field-level last-writer-wins. Deterministic: any device applying the
 * same set of operations in any order reaches the same state.
 */
export function applyOperation(dataset: Dataset, operation: SyncOperation): ApplyResult {
  const current = getEntity(dataset, operation.entityType, operation.entityId);
  const base: Record<string, unknown> = current
    ? structuredClone(current)
    : { ...entityDefaults(operation.entityType), id: operation.entityId, createdAt: operation.clock.wallTime, updatedAt: operation.clock.wallTime, fieldClocks: {} };
  const clocks = base["fieldClocks"] as Record<string, HybridClock>;
  let changed = !current;
  // createdAt is the earliest clock that touched the entity, so every device agrees on it.
  if (operation.clock.wallTime < Number(base["createdAt"])) {
    base["createdAt"] = operation.clock.wallTime;
    changed = true;
  }

  for (const [field, value] of Object.entries(operation.patch)) {
    if (immutableFields.has(field)) continue;
    const fieldClock = clocks[field];
    if (fieldClock && compareClocks(operation.clock, fieldClock) <= 0) continue;
    base[field] = value;
    clocks[field] = operation.clock;
    changed = true;
  }
  if (!changed) return { dataset, changed: false, rejected: false };
  base["updatedAt"] = Math.max(Number(base["updatedAt"]), operation.clock.wallTime);

  const parsed = entitySchemas[operation.entityType].safeParse(base);
  if (!parsed.success) return { dataset, changed: false, rejected: true };
  return { dataset: withEntity(dataset, operation.entityType, parsed.data), changed: true, rejected: false };
}

export function applyOperations(dataset: Dataset, operations: readonly SyncOperation[]): Dataset {
  return operations.reduce((next, operation) => applyOperation(next, operation).dataset, dataset);
}

function withEntity<T extends EntityType>(dataset: Dataset, type: T, entity: EntityMap[T]): Dataset {
  const kind: EntityType = type;
  if (kind === "settings") return { ...dataset, settings: entity as Settings };
  const key = collectionOf[kind];
  const records: Record<string, unknown> = dataset[key];
  return { ...dataset, [key]: { ...records, [entity.id]: entity } };
}

/** Creates operations stamped with this device's monotonically increasing clock. */
export class OperationFactory {
  private last: HybridClock | undefined;

  constructor(readonly deviceId: string, private readonly now: () => number = Date.now) {}

  /** Advance past a clock seen from another device so local edits always order after it. */
  observe(clock: HybridClock): void {
    this.last = tickClock(this.deviceId, this.last, clock, this.now());
  }

  op(entityType: EntityType, entityId: string, patch: Record<string, unknown>): SyncOperation {
    this.last = tickClock(this.deviceId, this.last, undefined, this.now());
    return { opId: `op_${crypto.randomUUID()}`, deviceId: this.deviceId, entityType, entityId, clock: this.last, patch };
  }
}

/**
 * Builds operations that revert `operations` against the state they were applied to. Entities
 * that did not exist before are discarded (hidden, not sent to Trash).
 */
export function invertOperations(before: Dataset, operations: readonly SyncOperation[], factory: OperationFactory, now: number): SyncOperation[] {
  const inverse: SyncOperation[] = [];
  const seen = new Set<string>();
  for (const operation of [...operations].reverse()) {
    const key = `${operation.entityType}:${operation.entityId}`;
    const previous = getEntity(before, operation.entityType, operation.entityId) as Record<string, unknown> | undefined;
    if (!previous) {
      if (!seen.has(key)) inverse.push(factory.op(operation.entityType, operation.entityId, { deletedAt: now, discardedAt: now }));
    } else {
      const patch = Object.fromEntries(Object.keys(operation.patch).filter((field) => !immutableFields.has(field)).map((field) => [field, previous[field]]));
      inverse.push(factory.op(operation.entityType, operation.entityId, patch));
    }
    seen.add(key);
  }
  return inverse;
}

/** Entities that are neither in Trash nor discarded. */
export function isLive(entity: { deletedAt: number | null; discardedAt: number | null }): boolean {
  return entity.deletedAt === null && entity.discardedAt === null;
}
